import React, { useCallback, useEffect, useState } from 'react';

import AbsenceModal, { absenceReasonLabel } from './AbsenceModal';
import { Absence, ABSENCE_REASONS } from '../../../models/monthly-declaration.model';
import { DeclarationDA } from '../../../services/admin/declaration.service';
import { dateFormat } from '../../../scripts/date.time.format.script';
import { countSwissdecDays } from '../../../scripts/swissdec-days';

interface AbsenceReportProps {
  companyId: string;
  employeeId: string;
  lang: any;
  editable?: boolean;
}

const AbsenceReport: React.FunctionComponent<AbsenceReportProps> = ({
  companyId, employeeId, lang, editable = false,
}) => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [reasonFilter, setReasonFilter] = useState<string>('');
  const [absences, setAbsences] = useState<Absence[] | null>(null);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [editingAbsence, setEditingAbsence] = useState<Absence | null>(null);
  const [absenceModalOpen, setAbsenceModalOpen] = useState(false);

  const loadAbsences = useCallback(async () => {
    setLoading(true);
    try {
      const response = await DeclarationDA.getAbsences(companyId, employeeId, {
        from: `${year}-01-01`,
        to: `${year}-12-31`,
      });
      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : response.data?.items ?? [];
        setAbsences(data);
      } else {
        setAbsences([]);
      }
    } catch {
      setAbsences([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, employeeId, year]);

  useEffect(() => {
    loadAbsences();
  }, [loadAbsences]);

  const filteredAbsences = absences?.filter(a =>
    !reasonFilter || a.reason === reasonFilter
  ) ?? [];

  // Totals by reason
  const totalsByReason = new Map<string, number>();
  for (const a of filteredAbsences) {
    const days = a.to ? countSwissdecDays(a.from, a.to) : 0;
    totalsByReason.set(a.reason, (totalsByReason.get(a.reason) ?? 0) + days);
  }

  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    yearOptions.push(y);
  }

  const handleOpenModal = (absence?: Absence | null) => {
    setEditingAbsence(absence ?? null);
    setAbsenceModalOpen(true);
  };

  const handleAbsenceSuccess = () => {
    setAbsenceModalOpen(false);
    loadAbsences();
  };

  return (
    <div className="card mt-5">
      <div className="card-header">
        <div className="card-title">
          <h4 className="fw-bolder">{lang.labels.absenceReport}</h4>
        </div>
        <div className="card-toolbar d-flex gap-2">
          <select
            className="form-select form-select-sm w-auto"
            value={year}
            onChange={e => setYear(Number(e.target.value))}
          >
            {yearOptions.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select
            className="form-select form-select-sm w-auto"
            value={reasonFilter}
            onChange={e => setReasonFilter(e.target.value)}
          >
            <option value="">{lang.labels.allReasons}</option>
            {ABSENCE_REASONS.map(r => (
              <option key={r} value={r}>{absenceReasonLabel(lang, r)}</option>
            ))}
          </select>
          {editable && (
            <button className="btn btn-sm btn-light-primary" onClick={() => handleOpenModal()}>
              <i className="bi bi-plus-lg me-1"></i>{lang.labels.addAbsence}
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <span className="spinner-border text-primary"></span>
          </div>
        ) : filteredAbsences.length === 0 ? (
          <p className="text-muted fw-bold text-center py-5">{lang.labels.noAbsences}</p>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table align-middle table-row-dashed mb-2">
                <thead className="border-bottom">
                  <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                    <th className="min-w-120px">{lang.labels.absenceReason}</th>
                    <th className="min-w-100px">{lang.labels.absenceFrom}</th>
                    <th className="min-w-100px">{lang.labels.absenceTo}</th>
                    <th className="min-w-80px">{lang.labels.absenceDays}</th>
                    <th className="min-w-80px">{lang.labels.incapacityRate}</th>
                    <th className="min-w-80px">{lang.labels.salaryContinuationRate}</th>
                    {editable && <th className="w-50px"></th>}
                  </tr>
                </thead>
                <tbody className="text-secondary fw-bold">
                  {filteredAbsences.map(a => {
                    const days = a.to ? countSwissdecDays(a.from, a.to) : null;
                    const isOngoing = !a.to;
                    return (
                      <tr key={a.id}>
                        <td>
                          <span className="badge badge-light-danger">{absenceReasonLabel(lang, a.reason)}</span>
                        </td>
                        <td>{dateFormat(lang.code, a.from)}</td>
                        <td>
                          {isOngoing ? (
                            <span className="badge badge-light-info">{lang.labels.absenceOngoing}</span>
                          ) : (
                            dateFormat(lang.code, a.to!)
                          )}
                        </td>
                        <td>{days ?? '-'}</td>
                        <td>{a.incapacity_rate}%</td>
                        <td>{a.salary_continuation_rate}%</td>
                        {editable && (
                          <td>
                            <button
                              className="btn btn-sm btn-icon btn-light-warning"
                              title={lang.labels.editAbsence}
                              onClick={() => handleOpenModal(a)}
                            >
                              <i className="bi bi-pencil fs-8"></i>
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals by reason */}
            {totalsByReason.size > 0 && (
              <div className="mt-5">
                <h6 className="fw-bolder text-muted mb-3">{lang.labels.totalByReason}</h6>
                <div className="d-flex flex-wrap gap-3">
                  {Array.from(totalsByReason.entries()).map(([reason, days]) => (
                    <div key={reason} className="d-flex align-items-center">
                      <span className="badge badge-light-danger me-2">{absenceReasonLabel(lang, reason)}</span>
                      <span className="fw-bold">
                        {(lang.labels.daysRetained || '{days} day(s)').replace('{days}', String(days))}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Absence modal */}
      {absenceModalOpen && (
        <AbsenceModal
          companyId={companyId}
          employeeId={employeeId}
          lang={lang}
          absence={editingAbsence}
          onSuccess={handleAbsenceSuccess}
          onClose={() => setAbsenceModalOpen(false)}
        />
      )}
    </div>
  );
};

export default AbsenceReport;
